import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useRouteContext } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import type { audience } from '@bb/contracts';
import { Button, CopyId, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type DependentView, type SegmentCampaignDraft, type SegmentView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { useRecordView } from '../home/useRecordView.ts';
import { EditorShell } from '../editor/EditorShell.tsx';
import { useEditorState } from '../editor/useEditorState.ts';
import { useObjectTab } from '../tabs/objectTabs.ts';
import { AudienceBuilder, emptyAudience } from '../audience/AudienceBuilder.tsx';
import { AudiencePopulation } from '../audience/AudiencePopulation.tsx';
import { ExportDialog } from '../exports/ExportDialog.tsx';
import { ObjectInUseDialog } from '../operations/ObjectInUseDialog.tsx';
import { WhereUsedPanel } from '../operations/WhereUsedPanel.tsx';
import { TagPicker } from '../tags/TagPicker.tsx';
import { SegmentHistoryChart } from './SegmentHistoryChart.tsx';
import { SegmentRevisionsDrawer } from './SegmentRevisionsDrawer.tsx';

interface Draft {
  name: string;
  description: string;
  definition: audience.AudienceDefinition;
}

const draftOf = (s: SegmentView): Draft => ({ name: s.name, description: s.description ?? '', definition: s.definition });
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/**
 * Segment editor (BB-46): name, description, tags, the audience with its size and User Lookup, Save / Revert with an
 * unsaved-changes guard, API id, revisions, duplicate, archive and «Create campaign». `new` creates a segment.
 */
export function SegmentEditorPage() {
  const { segmentId } = useParams({ from: '/env/$environmentId/audience/segments/$segmentId' });
  // A new id (created, duplicated) starts a fresh editor with its own draft.
  return <SegmentEditor key={segmentId} />;
}

function SegmentEditor() {
  const { environmentId, segmentId } = useParams({ from: '/env/$environmentId/audience/segments/$segmentId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/audience/segments/$segmentId' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const isNew = segmentId === 'new';
  useRecordView(environmentId, 'segment', segmentId, !isNew);
  const key = ['segment', environmentId, segmentId];
  const segment = useQuery({ queryKey: key, queryFn: () => api.getSegment(environmentId, segmentId), enabled: !isNew });
  const [blank] = useState<Draft>(() => ({ name: '', description: '', definition: emptyAudience() }));
  const server = useMemo(() => (isNew ? blank : segment.data ? draftOf(segment.data) : undefined), [isNew, blank, segment.data]);
  const editor = useEditorState(server);
  const draft = editor.draft;
  const setDraft = editor.setDraft;
  const archived = segment.data?.status === 'archived';
  const canEdit = has('segments.edit') && !archived;
  const dirty = editor.isDirty;
  // Leaving right after a save (a created segment opens under its id) is not leaving unsaved changes.
  const saved = useRef(false);
  useObjectTab(
    environmentId,
    segment.data ? { type: 'segment', id: segment.data.id, title: segment.data.name, url: `/env/${environmentId}/audience/segments/${segment.data.id}`, listUrl: `/env/${environmentId}/audience/segments` } : null,
  );
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [campaign, setCampaign] = useState<SegmentCampaignDraft | null>(null);

  const refresh = async (row: SegmentView) => {
    queryClient.setQueryData(['segment', environmentId, row.id], row);
    void queryClient.invalidateQueries({ queryKey: ['segments', environmentId] });
    void queryClient.invalidateQueries({ queryKey: ['segment-revisions', environmentId, row.id] });
  };
  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const body = { name: d.name.trim(), description: d.description.trim() || null, definition: d.definition };
      return isNew ? api.createSegment(environmentId, body) : api.updateSegment(environmentId, segmentId, { revision: segment.data!.revision, ...body });
    },
    onSuccess: async (result) => {
      await refresh(result);
      editor.markSaved(draftOf(result));
      if (isNew) {
        saved.current = true;
        await navigate({ to: '/env/$environmentId/audience/segments/$segmentId', params: { environmentId, segmentId: result.id }, replace: true });
      }
    },
  });
  const action = useMutation({
    mutationFn: async (kind: 'archive' | 'restore' | 'duplicate') =>
      kind === 'archive' ? api.archiveSegment(environmentId, segmentId) : kind === 'restore' ? api.restoreSegment(environmentId, segmentId) : api.duplicateSegment(environmentId, segmentId),
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'object_in_use') setInUse((error.problem.dependents as DependentView[] | undefined) ?? []);
    },
    onSuccess: async (result, kind) => {
      setWarnings('warnings' in result ? (result.warnings as DependentView[]) : []);
      void queryClient.invalidateQueries({ queryKey: ['where-used', environmentId] });
      if (kind === 'duplicate') {
        void queryClient.invalidateQueries({ queryKey: ['segments', environmentId] });
        await navigate({ to: '/env/$environmentId/audience/segments/$segmentId', params: { environmentId, segmentId: result.id } });
        return;
      }
      await refresh(result);
      editor.markSaved(draftOf(result));
    },
  });
  const [inUse, setInUse] = useState<DependentView[] | null>(null);
  const [exporting, setExporting] = useState(false);
  const [warnings, setWarnings] = useState<DependentView[]>([]);
  const createCampaign = useMutation({ mutationFn: () => api.segmentCampaignDraft(environmentId, segmentId), onSuccess: setCampaign });
  const reload = async () => {
    const fresh = await segment.refetch();
    if (fresh.data) editor.markSaved(draftOf(fresh.data));
    save.reset();
  };
  const conflict = save.error instanceof ApiError && save.error.code === 'revision_conflict';

  if (!isNew && segment.isError) return <ErrorState title="Segment could not be loaded" message={errorText(segment.error)} onRetry={() => void segment.refetch()} />;
  if (!draft) return <p aria-busy="true">Loading segment…</p>;
  const set = (patch: Partial<Draft>) => editor.update(patch);

  return (
    <EditorShell dirty={dirty} allowLeave={() => saved.current} onSave={() => save.mutateAsync(draft)} canSave={canEdit && draft.name.trim() !== ''} objectLabel="segment">
    <section className="segment-editor">
      <h1>{isNew ? 'New segment' : draft.name || segment.data?.name}</h1>
      {archived && (
        <ReadOnlyBanner>
          This segment is archived.{' '}
          {has('segments.edit') && (
            <Button size="sm" onClick={() => action.mutate('restore')} disabled={action.isPending}>
              Restore
            </Button>
          )}
        </ReadOnlyBanner>
      )}
      {!has('segments.edit') && <ReadOnlyBanner>You can view this segment; editing needs the segments.edit permission.</ReadOnlyBanner>}
      {segment.data && (
        <p className="hint">
          <CopyId value={segment.data.apiId} label={`API id of ${segment.data.name}`} /> · Revision {segment.data.revision}
          {segment.data.updatedBy?.email ? ` · last saved by ${segment.data.updatedBy.email}` : ''}
        </p>
      )}
      <div className="stack-form">
        <label>
          Segment name
          <input value={draft.name} disabled={!canEdit} maxLength={200} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label>
          Description
          <textarea value={draft.description} disabled={!canEdit} maxLength={1000} onChange={(e) => set({ description: e.target.value })} />
        </label>
      </div>
      {segment.data && <TagPicker environmentId={environmentId} objectType="segment" objectId={segment.data.id} canEdit={canEdit} />}
      <AudienceBuilder environmentId={environmentId} value={draft.definition} readOnly={!canEdit} onChange={(definition) => set({ definition })} />
      <AudiencePopulation environmentId={environmentId} definition={draft.definition} />
      {segment.data && <SegmentHistoryChart environmentId={environmentId} segmentId={segment.data.id} />}
      {segment.data && <WhereUsedPanel environmentId={environmentId} objectType="segment" objectId={segment.data.id} />}

      <div className="segment-editor__actions" role="group" aria-label="Segment actions">
        {canEdit && (
          <>
            <Button onClick={() => save.mutate(draft)} disabled={!dirty || save.isPending || !draft.name.trim()}>
              {isNew ? 'Create segment' : 'Save'}
            </Button>
            {!isNew && (
              <Button variant="ghost" onClick={editor.reset} disabled={!dirty || save.isPending}>
                Revert
              </Button>
            )}
          </>
        )}
        {!isNew && (
          <Button variant="ghost" onClick={() => setRevisionsOpen(true)}>
            Revisions
          </Button>
        )}
        {!isNew && has('segments.edit') && (
          <Button variant="ghost" onClick={() => action.mutate('duplicate')} disabled={action.isPending}>
            Duplicate
          </Button>
        )}
        {!isNew && canEdit && (
          <Button variant="ghost" onClick={() => action.mutate('archive')} disabled={action.isPending || dirty}>
            Archive
          </Button>
        )}
        {!isNew && !archived && has('data.export') && (
          <Button variant="ghost" onClick={() => setExporting(true)}>
            Export users
          </Button>
        )}
        {!isNew && !archived && has('campaigns.edit') && (
          <Button variant="ghost" onClick={() => createCampaign.mutate()} disabled={createCampaign.isPending || dirty}>
            Create campaign
          </Button>
        )}
      </div>
      {conflict ? (
        <div role="alert">
          Someone else saved this segment in the meantime (revision {String((save.error as ApiError).problem.currentRevision ?? '?')}). Your changes are not saved.{' '}
          <Button size="sm" onClick={() => void reload()}>
            Reload their version
          </Button>
        </div>
      ) : (
        save.isError && <p role="alert">{errorText(save.error)}</p>
      )}
      {action.isError && !inUse && <p role="alert">{errorText(action.error)}</p>}
      {warnings.length > 0 && (
        <div role="status">
          Archived. These drafts still use it and need another audience before launch:
          <ul>
            {warnings.map((w) => (
              <li key={`${w.objectType}:${w.objectId}`}>{w.name}</li>
            ))}
          </ul>
        </div>
      )}
      {inUse && <ObjectInUseDialog open onOpenChange={(open) => !open && setInUse(null)} what={`“${segment.data?.name ?? draft.name}”`} dependents={inUse} />}
      {campaign && (
        <p role="status">
          {campaign.available ? (
            <a href={campaign.wizardUrl}>Open the campaign wizard</a>
          ) : (
            'The campaign wizard is not available yet. The draft audience — members of this segment — is ready for it.'
          )}
        </p>
      )}
      {!isNew && segment.data && <ExportDialog environmentId={environmentId} target={{ kind: 'segment', segmentId: segment.data.id }} title={`Export users of «${segment.data.name}»`} open={exporting} onOpenChange={setExporting} />}
      {!isNew && (
        <SegmentRevisionsDrawer
          environmentId={environmentId}
          segmentId={segmentId}
          open={revisionsOpen}
          onOpenChange={setRevisionsOpen}
          {...(canEdit
            ? {
                onRestore: (r) => {
                  setDraft({ name: r.name, description: r.description ?? '', definition: r.definition });
                  setRevisionsOpen(false);
                },
              }
            : {})}
        />
      )}
    </section>
    </EditorShell>
  );
}
